// Collection labels are authored in a small editor and stored as HTML, then
// rendered to every customer. Admin-authored is not the same as trusted — the
// value round-trips through the database and back into the page, so it is
// whitelisted on the way in AND on the way out rather than being handed straight
// to dangerouslySetInnerHTML.

// SUB/SUP carry the "Deco by EGGER" look: the maker sits at the foot of the
// brand. They are stored as real tags rather than styled spans so the markup in
// the database says what it means and renders the same anywhere it is dropped.
const ALLOWED_TAGS = new Set([
    'B', 'STRONG', 'I', 'EM', 'SPAN', 'SMALL', 'BR', 'SUB', 'SUP',
]);

// Only typography. Nothing that can position, size or paint over other elements.
const ALLOWED_STYLE_PROPS = new Set([
    'font-size',
    'font-weight',
    'font-style',
    'font-family',
    'letter-spacing',
    'text-transform',
]);

// url(), expression() and friends never belong in a label's style value.
const UNSAFE_STYLE_VALUE = /url\s*\(|expression\s*\(|javascript:|@import/i;

const cleanStyle = (styleValue) =>
    String(styleValue || '')
        .split(';')
        .map((decl) => decl.trim())
        .filter(Boolean)
        .filter((decl) => {
            const [prop, ...rest] = decl.split(':');
            const value = rest.join(':');
            if (!prop || !value) return false;
            if (!ALLOWED_STYLE_PROPS.has(prop.trim().toLowerCase())) return false;
            return !UNSAFE_STYLE_VALUE.test(value);
        })
        .join('; ');

const scrub = (node) => {
    [...node.childNodes].forEach((child) => {
        if (child.nodeType === Node.TEXT_NODE) return;

        if (child.nodeType !== Node.ELEMENT_NODE) {
            child.remove();
            return;
        }

        // Drop these outright — unwrapping would keep their text content, which
        // is exactly what you do not want from a <script>.
        if (['SCRIPT', 'STYLE', 'IFRAME', 'OBJECT', 'EMBED'].includes(child.tagName)) {
            child.remove();
            return;
        }

        scrub(child);

        if (!ALLOWED_TAGS.has(child.tagName)) {
            // Unwrap: keep the words, lose the tag.
            child.replaceWith(...child.childNodes);
            return;
        }

        [...child.attributes].forEach((attr) => {
            if (attr.name.toLowerCase() !== 'style') {
                child.removeAttribute(attr.name);
                return;
            }
            const safe = cleanStyle(attr.value);
            if (safe) child.setAttribute('style', safe);
            else child.removeAttribute('style');
        });
    });
};

// For text that is about to be dropped into a template string of markup —
// a brand name pulled out of a label is data, not tags.
export const escapeHtml = (text) =>
    String(text == null ? '' : text)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');

export const sanitizeRichText = (html) => {
    if (!html) return '';
    const doc = new DOMParser().parseFromString(String(html), 'text/html');
    scrub(doc.body);
    return doc.body.innerHTML;
};

// True when the value carries formatting. Used to decide whether a heading keeps
// its blanket uppercase — a plain label should look exactly as it always has,
// while an authored one is shown as written.
export const isRichText = (html) => /<[a-z][\s\S]*>/i.test(String(html || ''));

export const richTextToPlain = (html) => {
    if (!html) return '';
    const doc = new DOMParser().parseFromString(String(html), 'text/html');
    return doc.body.textContent || '';
};

// Collection labels are stored as "BRAND by MAKER" and the design wants the
// brand large with the maker at its foot. Only two things are read out of the
// stored value: the words, and which of them is the maker. The sizing belongs
// here, not to whoever typed the label — one heading authored with its own font
// sizes read completely differently from the ones beside it, and a row of them
// has to match. Admin and visualizer both call this so a label previews exactly
// as customers will see it.
const splitCollectionLabel = (raw) => {
    const doc = new DOMParser().parseFromString(String(raw), 'text/html');
    // Read the words before touching the tree — the <sub> check below removes a
    // node, and the fallback still needs the whole label.
    const text = (doc.body.textContent || '').trim();

    // An authored label says where the maker is by putting it in <sub>.
    const foot = doc.body.querySelector('sub, sup, small');
    if (foot) {
        const maker = (foot.textContent || '').trim();
        foot.remove();
        const brand = (doc.body.textContent || '').trim();
        if (brand && maker) return { brand, maker };
    }

    // Otherwise read it off the words themselves.
    const split = text.match(/^(.*?)\s+(by\s+.*)$/i);
    if (!split) return { brand: text, maker: '' };
    return { brand: split[1].trim(), maker: split[2].trim() };
};

// Returns markup plus whether it is styled, so a caller also learns not to force
// uppercase — that would turn the "by" into "BY".
export const collectionLabelMarkup = (raw) => {
    if (!raw) return { html: '', styled: false };

    const { brand, maker } = splitCollectionLabel(raw);
    if (!brand) return { html: '', styled: false };
    if (!maker) return { html: escapeHtml(brand), styled: false };

    // Brand and maker sit on one line at the same size; only the joining "by"
    // is set lowercase. Uppercase is applied per word here because
    // the caller drops its blanket uppercase for styled labels.
    const makerName = maker.replace(/^by\s+/i, '');
    return {
        html: sanitizeRichText(
            `<span style="text-transform:uppercase">${escapeHtml(brand)}</span> ` +
            `<span style="text-transform:lowercase">by</span> ` +
            `<span style="text-transform:uppercase">${escapeHtml(makerName)}</span>`
        ),
        styled: true,
    };
};
